import { useState, useEffect, useMemo, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { NodeListItem } from '@/components/nodes'
import type { Message } from '@/types'
import { Inbox, Loader2 } from 'lucide-react'
import { messageService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

interface MessageInboxProps {
  onMessageSelect?: (message: Message) => void
  selectedMessageId?: string
  preFilteredMessages?: Message[] // Optional pre-filtered messages from parent
  searchTerm?: string // Optional search term from parent
}

export function MessageInbox({
  onMessageSelect,
  preFilteredMessages,
  searchTerm,
}: MessageInboxProps) {
  const { user } = useAuth()
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)

  const loadMessages = useCallback(async () => {
    if (!user) return

    setLoading(true)
    try {
      const userMessages = await messageService.getMessagesForUser(user.id)
      setMessages(userMessages)
    } catch (error) {
      console.error('Failed to load messages:', error)
      toast.error('Failed to load messages')
    } finally {
      setLoading(false)
    }
  }, [user])

  // Use pre-filtered messages if provided, otherwise load all messages
  useEffect(() => {
    if (preFilteredMessages) {
      setMessages(preFilteredMessages)
      setLoading(false)
    } else {
      loadMessages()
    }
  }, [preFilteredMessages, loadMessages])

  const filteredMessages = useMemo(() => {
    let filtered = [...messages]

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (message) =>
          message.name.toLowerCase().includes(term) ||
          message.description.toLowerCase().includes(term) ||
          message.creator.name.toLowerCase().includes(term) ||
          message.creator.email.toLowerCase().includes(term)
      )
    }

    return filtered
  }, [messages, searchTerm])

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Message List */}
      <div className="space-y-2">
        {filteredMessages.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-muted-foreground">
              <Inbox className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p className="text-lg font-medium mb-2">No messages found</p>
              <p className="text-sm">
                {searchTerm ? 'Try adjusting your filters' : 'Your inbox is empty'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredMessages.map((message) => (
            <NodeListItem
              key={message.id}
              item={message}
              context="message"
              config={{
                showCreator: true,
                showDate: true,
                showReplyCount: true,
                showWorkspace: true,
                showProjects: true,
                showTasks: true,
                showDocuments: true,
                showDescription: true,
                showRelationshipCounts: false,
                showReadStatus: true,
                showMessageType: true,
                currentUserId: user?.id,
                onClick: () => onMessageSelect?.(message),
              }}
            />
          ))
        )}
      </div>
    </div>
  )
}
